import React, {useEffect, useMemo, useState} from 'react';
import {Box, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles} from '@material-ui/core';
import {IconClose, IconHeart} from '../../../components/Icons';
import {rieltor} from '../../../styles/theme';
import Typo from '../../../components/Typo';
import {FormattedMessage} from 'react-intl';
import {ButtonGray, ButtonPurple, ButtonWhite} from '../../../components/Buttons';
import RouterLink from '../../common/RouterLink';
import {AlertViewSettingKey, LocalStorageKeys, RoleId} from '../../../common/types';
import {useCurrentUser} from '../../../hooks/user';
import {createProfileEditUrl, createProfileSummaryUrl} from '../../../utils/navigation';
import {useLocalStorage} from '@rehooks/local-storage';
import {isSameHour} from 'date-fns';
import {UsersAPI} from '../../../services/UsersAPI';
import {fetchUserInfo} from '../../../store/user/actions';
import {useDispatch, useSelector} from 'react-redux';
import {localStoreSelector} from '../../../store/local/selectors';
import {setLocalItem} from '../../../store/local/reducer';
import {IsAlertVisible} from '../../../utils';
import {APP_REVIEW_DIALOG, closeDialog, getDialogName} from '../../../store/offers/reducers/actionDialogs';
import {FeedbackType} from '../../../components/FeedbackForm/types';
import {FeedbackForm, FeedbackFormDialog} from '../../../components/FeedbackForm';
import {useIsRieltorApp} from '../../../hooks/useIsRieltorApp';
import {Star, Stars} from '@material-ui/icons';
import {hasAppReviewCapability, requestAppReview} from '../../../common/ios';
import ReactGA from 'react-ga';
import {iosAppReviewSelector, seoPatternsSelector} from '../../../store/global/selectors';

const ALERT_ID_APP_REVIEW = '420';
const useStyles = makeStyles((theme) => ({
  popupWrapper: {
    '& .MuiDialog-paper': {
      maxWidth: '650px !important',
      margin: '5px',
    },
    '& .MuiDialogContent-root': {
      padding: '0px 16px 0',
    },
    '& button': {
      borderRadius: '8px',
    },
  },
  actionButton: {
    width: '165px',
    height: '59px',
    fontSize: '16px',
    fontWeight: 700,
    padding: '0',

    '&:first-child': {
      marginRight: '20px',
    },
  },
  stars: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  star: {
    width: 32,
    height: 32,
  },
  starButton: {
    width: '80%',
    height: '40px',
    margin: '20px 10% 44px 10%',
  },
  title1: {
    lineHeight: '30px',
  },
  title2: {
    lineHeight: '30px',
  },
}));
const RieltorAppReview = () => {
  const classes = useStyles();
  const usersAPI = useMemo(() => new UsersAPI(), []);
  const dispatch = useDispatch();
  const dialogName = useSelector(getDialogName);
  const isRieltorApp = useIsRieltorApp();
  const iosAppReview = useSelector(iosAppReviewSelector);


  const user = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isLikeOpened, setIsLikeOpened] = useState(false);

  const isAlertVisible = IsAlertVisible(user, ALERT_ID_APP_REVIEW, null);
  const isDialogOpen = dialogName === APP_REVIEW_DIALOG;

  useEffect(() => {
    if (!isRieltorApp || !isAlertVisible || !isDialogOpen || !hasAppReviewCapability()) {
      return;
    }
    ReactGA.event({
      category: 'app_review',
      action: 'show',
    });
    setOpen(true);
    try {
      alertCloseTemporarily();
      // usersAPI.updateAlertStatus({id: ALERT_ID_APP_REVIEW, firstView: true});
      // dispatch(fetchUserInfo());
      // dispatch(setLocalItem(LocalStorageKeys.ForceIpnAlert, true));
    } catch (e) {
    }
  }, [isAlertVisible, isRieltorApp, isDialogOpen]);

  const alertClosePermanently = async () => {
    try {
      await usersAPI.updateAlertStatus({id: ALERT_ID_APP_REVIEW, isManuallyClosed: true});
      dispatch(fetchUserInfo());
    } catch (e) {
      console.error('Failed to close alert permanently', e);
    }
  }

  const alertCloseTemporarily = async () => {
    try {
      await usersAPI.updateAlertStatus({id: ALERT_ID_APP_REVIEW, firstView: true});
      dispatch(fetchUserInfo());
    } catch (e) {
      console.error('Failed to close alert temporarily', e);
    }
  }

  const handleLikeClick = () => {
    setOpen(false);
    setIsLikeOpened(true);

    ReactGA.event({
      category: 'app_review',
      action: 'like_click',
    });
  };

  const handleDislikeClick = () => {
    setOpen(false);
    setShowFeedback(true);
    alertCloseTemporarily();


    ReactGA.event({
      category: 'app_review',
      action: 'dislike_click',
    });
  };

  const handleReviewClick = () => {
    setIsLikeOpened(false);

    ReactGA.event({
      category: 'app_review',
      action: 'review_click',
    });

    alertClosePermanently();
  };

  const handleLikeClose = () => {
    setIsLikeOpened(false);

    ReactGA.event({
      category: 'app_review',
      action: 'like_close',
    });

    alertCloseTemporarily();

  };

  const handleReviewClose = () => {
    setOpen(false);

    ReactGA.event({
      category: 'app_review',
      action: 'review_close',
    });

    alertCloseTemporarily();
  };

  if (user === null) {
    return null;
  }

  return (
      <>
        <Dialog
            className={classes.popupWrapper}
            open={open}
        >
          <DialogTitle style={{padding: '8px 16px'}}>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <div>&nbsp;</div>

              <IconClose
                  style={{cursor: 'pointer', color: rieltor.gray4}}
                  onClick={handleReviewClose}
              />
            </Box>
          </DialogTitle>

          <DialogContent>
            <Typo variant={'title1'} mb={4} className={classes.title1}>
              {iosAppReview?.mainTitle || 'Як вам наш додаток Rieltor від ЛУН?'}
            </Typo>
          </DialogContent>

          <DialogActions style={{justifyContent: 'center', padding: '0 16px 20px 16px'}}>
            <ButtonGray onClick={handleDislikeClick} size={'large'} className={classes.actionButton}>
              Не подобається
            </ButtonGray>
            <ButtonPurple onClick={handleLikeClick} size={'large'} className={classes.actionButton}>
              Подобається
            </ButtonPurple>
          </DialogActions>
        </Dialog>
        {showFeedback &&
          <FeedbackFormDialog
            type={FeedbackType.AppReview}
            titleIcon={<IconHeart/>}
            isOpened={showFeedback}
            minLength={5}
            onClose={() => setShowFeedback(false)}
            question={iosAppReview?.feedbackText || 'Нам дуже важливий ваш відгук. Що ми можемо зробити краще?'}
            title={'Залишити відгук'}
          />
        }
        {isLikeOpened && <RieltorAppReviewLike onLike={handleReviewClick} onClose={handleLikeClose}/>}
      </>
  );
};

export default RieltorAppReview;

const RieltorAppReviewLike = ({onLike, onClose}: { onLike: () => void, onClose: () => void }) => {
  const classes = useStyles();
  const iosAppReview = useSelector(iosAppReviewSelector);
  const handleLikeClickAppStore = () => {
    // Open App Store link
    onLike();
    requestAppReview();
  };

  const handleCloseDialog = () => {
    // Close dialog
    // You can use Redux or any state management to close the dialog
    onClose();
  };

  return (
      <Dialog
          className={classes.popupWrapper}
          open={true}
      >
        <DialogTitle style={{padding: '8px 16px'}}>
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <div>&nbsp;</div>

            <IconClose
                style={{cursor: 'pointer', color: rieltor.gray4}}
                onClick={handleCloseDialog}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typo variant={'title2'} mb={3} className={classes.title2}>
            {iosAppReview?.reviewTitle || 'Будь ласка, оцініть наш додаток Rieltor від ЛУН в App Store'}
          </Typo>
        </DialogContent>

        <Box className={classes.stars}>
          {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className={classes.star}/>
          ))}
        </Box>

        <ButtonPurple
            className={classes.starButton}
            onClick={handleLikeClickAppStore}
        >
          Оцінити
        </ButtonPurple>
      </Dialog>
  );
};

